import { Target } from "@org-avp/avp-avengers-tmm-service-api";
import { ElectronAPI, getElectronAPI, NodeInfoType, runsInsideElectron } from "@org-avp/avp-avengers-ui-framework-electron-api";
import { ContextAction, MessageModalType, MsgModalModel } from "@org-avp/avp-avengers-ui-shared-components";
import { TargetAssignmentIcon } from "@org-avp/avp-avengers-ui-shared-icons";
import { stringifyTarget } from "@org-avp/avp-avengers-ui-shared-utils";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface TargetAssignHelper {
  assignTargetAction: ContextAction;
}

export const useTargetAssignHelper = (target?: Target, setMsgModalModel?: (msgModalModel: MsgModalModel) => boolean): TargetAssignHelper => {
  const { t } = useTranslation("asConnector");

  const electronAPI: ElectronAPI | undefined = useMemo(() => runsInsideElectron() ? getElectronAPI() : undefined, []);

  const isAssignTargetEnabled = useMemo(
    () => electronAPI != null && target != null,
    [electronAPI, target],
  );

  const [isAssignTargetInProgress, setIsAssignTargetInProgress] = useState(false);
  const setModalUserFeedback = useCallback((msgModalModel: MsgModalModel) => {
    if (setMsgModalModel) {
      setMsgModalModel(msgModalModel);
    }
  }, [setMsgModalModel]);

  const assignTarget = useCallback(() => {
    if (electronAPI && target && !isAssignTargetInProgress) {
      setIsAssignTargetInProgress(true);
      electronAPI.invoke.asConnectorNodeGetSelection().then((nodeInfos) => {
        const relevantNodeInfos = nodeInfos.filter((it) => isTargetAssignmentForNodeTypePermitted(it.NodeInfoType));

        if (relevantNodeInfos.length > 0) {
          const targetName = stringifyTarget(target.colorFamilyEnabled, target);
          const pRed = Math.max(0, Math.min(target.color.red / 255, 1));
          const pGreen = Math.max(0, Math.min(target.color.green / 255, 1));
          const pBlue = Math.max(0, Math.min(target.color.blue / 255, 1));
          const pTransparency = Math.max(0, Math.min((255 - target.color.alpha) / 255, 1));

          // CWI: "shininess=30" in DeltaGen.
          // AsConnector documentation: shininess in [0; 1].
          // AsConnector implementation: take shininess as it was passed (source refers to DBSKSIPE-5382): Shininess range in DeltaGen is [2; 10000].
          // A value x in [0;1] is mapped to [2; 10000] in frontend-electron with f(x)=rangeMin+x*(rangeMax-rangeMin).
          // => pShininess = (shininess - shininessRangeMin) / (shininessRangeMax - shininessRangeMin)
          // with shininessRangeMin=2 and shininessRangeMax=10000.
          const pShininess = (30 - 2) / (10000 - 2);

          const targetAssignmentPromises = relevantNodeInfos.map((nodeInfo) => electronAPI.invoke
            .asConnectorMaterialAssignToNode(nodeInfo, targetName, pRed, pGreen, pBlue, pTransparency, pShininess, true));

          Promise.all(targetAssignmentPromises)
            .then((successStates) => {
              return successStates.reduce((successCount, success) => success ? successCount + 1 : successCount, 0);
            })
            .then((successCount) => {
              const selectedNodeCount = nodeInfos.length;
              const relevantNodeCount = relevantNodeInfos.length;
              const success = successCount === relevantNodeCount;

              if (successCount === relevantNodeCount && relevantNodeCount === selectedNodeCount) {
                console.log(`Successfully assigned target "${targetName}" to ${selectedNodeCount} selected nodes.`);
              } else {
                const type: MessageModalType = success ? "WARNING" : "ERROR";
                const title = t("assignTargetToSelectedNodes.hint.title");

                const targetRow = t("assignTargetToSelectedNodes.hint.message.target", { targetName });
                const selectedNodesRow = t("assignTargetToSelectedNodes.hint.message.selectedNodes", { count: selectedNodeCount });
                const relevantNodesRow = t("assignTargetToSelectedNodes.hint.message.relevantNodes", { count: relevantNodeCount });
                const targetAssignmentsRow = t("assignTargetToSelectedNodes.hint.message.targetAssignments", { count: successCount });

                const rows = { targetRow, selectedNodesRow, relevantNodesRow, targetAssignmentsRow };
                const message = t("assignTargetToSelectedNodes.hint.message.rows", rows);

                const detail = t("assignTargetToSelectedNodes.relevantNodesHint", { targetAssignmentNodeTypesStr });
                setModalUserFeedback({ type, title, message, detail });
              }
            })
            .catch((e) => {
              console.error("Failure during assignment of target to selected nodes in authoring system!", target, relevantNodeInfos, e);
              const title = t("assignTargetToSelectedNodes.error.title");
              const message = t("assignTargetToSelectedNodes.error.message");
              setModalUserFeedback({ type: "ERROR", title, message });
            });
        } else {
          console.info(`None of the ${nodeInfos.length} selected nodes is relevant for target assignment.`, nodeInfos);
          const title = t("assignTargetToSelectedNodes.noRelevantNodeSelected.title");
          const message = t("assignTargetToSelectedNodes.noRelevantNodeSelected.message");
          const detail = t("assignTargetToSelectedNodes.relevantNodesHint", { targetAssignmentNodeTypesStr });
          setModalUserFeedback({ type: "WARNING", title, message, detail });
        }
      }).catch((e) => {
        console.error("Unable to get selected nodes in authoring system for target assignment!", target, e);
        const title = t("assignTargetToSelectedNodes.error.title");
        const message = t("assignTargetToSelectedNodes.error.message");
        setModalUserFeedback({ type: "ERROR", title, message });
      }).finally(() => {
        setIsAssignTargetInProgress(false);
      });
    }
  }, [electronAPI, isAssignTargetInProgress, setModalUserFeedback, t, target]);

  const assignTargetAction = useMemo(
    () => new ContextAction({
      action: assignTarget,
      disabled: !isAssignTargetEnabled,
      textKey: "assignTarget",
      icon: <TargetAssignmentIcon/>,
    }),
    [assignTarget, isAssignTargetEnabled],
  );

  return useMemo(() => ({ assignTargetAction }), [assignTargetAction]);
};

const isTargetAssignmentForNodeTypePermitted: (nodeInfoType: NodeInfoType) => boolean = (nodeInfoType) => {
  switch (nodeInfoType) {
    case NodeInfoType.SHAPE:
    case NodeInfoType.SHELL:
      return true;
    default:
      return false;
  }
};

const targetAssignmentNodeTypesStr = Object.values(NodeInfoType).filter((it) => isTargetAssignmentForNodeTypePermitted(it)).join(", ");
